import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import { alpha, useTheme } from '@mui/material/styles'
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import { operationCodeAndName } from '../operationNameLabel'
import { formatDuration } from '../duration'
import { qualityTypeColumns, quantityOfType } from '../reportQualityColumns'
import { raisedColor } from '../detailSurfaces'
import { ExpansionHeadline } from '../ExpansionHeadline'
import CollapsedFieldsLine from './CollapsedFieldsLine'
import ProgressOperationDetails from './ProgressOperationDetails'
import { OperationQuantityCell, OperationTimeCell } from './operationRowCells'
import OperationStatusCell from './OperationStatusCell'
import ExpandRowButton from './ExpandRowButton'
import { CONTROL_RADIUS } from '../detailControls'
import { isBlank, EMPTY_DASH, ColumnHeader } from '../detailShared'

// S421 — cuántos materiales se consumen de entrada antes de plegar el resto. Se ha medido hasta 45
// en una sola operación: una tabla de 45 filas empuja los reportes fuera de la vista. Se PLIEGA, no
// se pagina — paginar obligaría a pasar páginas para comparar dos materiales de la misma operación.
const RESOURCES_SHOWN_FOLDED = 8

/**
 * El consumo tal como llega, en el idioma del usuario y SIN INVENTAR DECIMALES.
 *
 * ⚠️⚠️ DECIDIDO: NO SE FIJAN DECIMALES, y es una decisión, no un olvido. El JSON no transporta ceros
 * finales —`0.180` llega como `0.18`— así que la escala que tuviera la medida original NO VIAJA.
 * Rellenar a tres decimales pintaría «0,180», que afirma una precisión de milésimas que el dato no
 * afirma: `0.18` y `0.180` son el mismo número y distinta afirmación sobre la medida, y la segunda
 * no nos la han dado. Se enseña exactamente lo que se sabe.
 * 📌 `maximumFractionDigits: 6` no es para redondear: es el techo para no COMERSE dígitos reales
 * que `toLocaleString` recortaría a tres por defecto.
 */
const formatConsumption = (value, language) => {
  const number = Number(value)
  if (value === '' || value === null || value === undefined || !Number.isFinite(number)) return EMPTY_DASH
  return number.toLocaleString(language || undefined, { maximumFractionDigits: 6 })
}

const OperationResources = ({ jobReferenceId, resources }) => {
  const { t_, i18n } = useSmartTranslation()
  const [expanded, setExpanded] = useState(false)
  const rows = resources || []

  // Array SIEMPRE presente, vacío si no consumió nada: el vacío es un dato («no consumió»), no una
  // ausencia, así que se dice con palabras en vez de pintar una tabla sin filas.
  if (rows.length === 0) {
    return (
      <Typography data-testid={`operation-resources-empty-${jobReferenceId}`} variant='body2' sx={{ color: 'text.secondary' }}>
        {t_('No materials consumed')}
      </Typography>
    )
  }

  const visibles = expanded ? rows : rows.slice(0, RESOURCES_SHOWN_FOLDED)
  return (
    <Box data-testid={`operation-resources-${jobReferenceId}`}>
      <Table size='small' aria-label={t_('Materials consumed')}>
        <TableHead>
          <TableRow>
            <TableCell>{t_('Code')}</TableCell>
            <TableCell>{t_('Name')}</TableCell>
            <TableCell align='right'>{t_('Consumption')}</TableCell>
            <TableCell>{t_('Unit')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibles.map((resource) => (
            <TableRow key={resource.rawMaterialReferenceId} data-testid={`operation-resource-${resource.rawMaterialReferenceId}`}>
              <TableCell sx={{ fontFamily: 'monospace' }}>{resource.code || EMPTY_DASH}</TableCell>
              <TableCell>{resource.name || EMPTY_DASH}</TableCell>
              <TableCell align='right' data-testid='operation-resource-consumption' sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
                {formatConsumption(resource.consumption, i18n && i18n.language)}
              </TableCell>
              {/* ⚠️⚠️ LA UNIDAD AUSENTE SE DECLARA EN SU COLUMNA, no se esconde. Un consumo sin
                  unidad es «0,18 de algo»: si la columna desapareciera cuando falta, la cifra de al
                  lado se leería como si su unidad fuera obvia. Y no es un caso raro que haya que
                  imaginar: es null en 4 de 10 consumos en `mic_testing` — mirando solo la base del
                  PO (0 de 1.527) se habría concluido que el campo no existe. */}
              <TableCell data-testid='operation-resource-unit' data-absent={resource.unit ? 'false' : 'true'} sx={{ color: resource.unit ? 'text.primary' : 'text.disabled' }}>
                {resource.unit || EMPTY_DASH}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {/* ⚠️ Plegar NO es ocultar: el botón dice CUÁNTOS hay detrás, así que el total se lee sin abrir
          nada. `consumptionCount` NO se pinta: está para que quien dude de una suma vea de cuántos
          hechos sale, no para ocupar una columna. */}
      {rows.length > RESOURCES_SHOWN_FOLDED && (
        <Button
          data-testid={`operation-resources-toggle-${jobReferenceId}`}
          size='small'
          variant='outlined'
          onClick={() => setExpanded((prev) => !prev)}
          sx={{ mt: 1, textTransform: 'none', borderRadius: CONTROL_RADIUS, minHeight: 44 }}
        >
          {expanded ? t_('Show fewer materials') : t_('Show all {{count}} materials', { count: rows.length })}
        </Button>
      )}
    </Box>
  )
}

/**
 * §2.1 (nota n2 del PO) — EL ESTADO DE REPORTE DE CADA FILA, derivado de lo que N10 sirve por operación.
 *
 * 📌 S442 (D13, plan del 23/09): este estado se pintaba además como una barra de color al borde de
 * la fila. La barra se quitó; el estado queda ESCRITO, con su tono, debajo del nombre.
 *
 * ⚠️⚠️ NO SE REUTILIZA EL VOCABULARIO DE `operationStatus.js`, y no es descuido. Aquellos ocho
 * valores llegan en el campo `status` de N3 y responden a «¿qué está pasando con este job?». N10 no
 * trae ese campo —trae `done`, `pending` y `openIncidenceCount`—, así que aquí la pregunta es otra:
 * «¿cuánto de esta operación está reportado?». Atar los rótulos de allí haría que cambiar un estado
 * del bloque de Operaciones cambiara este estado sin que nadie lo pida, que es exactamente de lo que
 * aquel módulo se cuida: dos enums que se parecen no son el mismo enum.
 *
 * ⚠️ EL COLOR NUNCA VIAJA SOLO (§2.5): cada estado lleva su rótulo y el rótulo se pinta en la fila.
 *
 * ⚠️⚠️ LAS INCIDENCIAS NO SON UN ESTADO DE ESTE MAPA, y estuvieron a punto de serlo. §2.1 dice que
 * una operación con incidencias abiertas es BLOQUEO, y la tentación es devolver un sexto valor que
 * pinte la fila de rojo y ponga «Incidencias abiertas» de rótulo. Eso TAPARÍA cuánto está reportado
 * —el único dato que esta tabla viene a dar— y es el defecto que el bloque de Operaciones ya
 * corrigió con todas las letras: «los avisos ACOMPAÑAN al estado, no lo sustituyen». Así que son
 * dos ejes: el estado de reporte SIEMPRE se dice, y el bloqueo se le suma con su aviso escrito.
 */
const OPERATION_STATE_VIEWS = {
  // RN8 — el dato que no ha llegado es ATENCIÓN, no un cero: un gris aquí diría «no hay nada que
  // reportar», que es justo lo que no sabemos. (La barra de color se quitó en S442; el tono queda
  // en el texto del estado.)
  UNREPORTED: { labelKey: 'Not reported', text: 'warning.main' },
  FULLY_REPORTED: { labelKey: 'Fully reported', text: 'success.main' },
  PARTLY_REPORTED: { labelKey: 'Partly reported', text: 'info.main' },
  // ⚠️ Clave PROPIA y no la `Nothing reported yet` que ya existe: aquella es la píldora de
  // Operaciones sobre el TIEMPO sin reportar (su `title` es «dentro del teórico»), y ésta habla de
  // CANTIDAD. Comparten la frase por casualidad; atarlas haría que reescribir un aviso de tiempo
  // cambiara el rótulo de una cantidad.
  NOTHING_REPORTED_YET: { labelKey: 'No quantity reported yet', text: 'text.secondary' },
  // La rama NO TOMADA: ninguna pieza llegó a esta operación. No es «sin empezar» ni un 0% — no hay
  // nada que medir, la misma distinción que la columna de porcentaje protege desde el addendum 30 A.
  NOTHING_TO_REPORT: { labelKey: 'Nothing to report', text: 'text.secondary' }
}

/**
 * @param {{done: number|null, pending: number|null, reached: number|null}} row
 *   `done`/`pending` YA normalizados por la fila: `null` es «el dato no llegó», nunca 0.
 * @returns {string} una clave de `OPERATION_STATE_VIEWS`
 */
const operationReportingState = ({ done, pending, reached }) => {
  // ⚠️ ANTES QUE CUALQUIER CUENTA: sin sumandos no hay estado que derivar, y el gris de «nada que
  // reportar» afirmaría sobre la operación algo que no sabemos (RN8).
  if (done === null || pending === null) return 'UNREPORTED'
  if (reached === 0) return 'NOTHING_TO_REPORT'
  if (pending === 0) return 'FULLY_REPORTED'
  if (done === 0) return 'NOTHING_REPORTED_YET'
  return 'PARTLY_REPORTED'
}

// CU-L9/CU-L10 — Progress block: milestones, summary (H8: literally the header's quantity
// breakdown, no recalculation), per-operation progress (RN62, same source as the Operations
// dropdown) and the shop-floor report list (RN63/RN63.1, paginated, no time-declared column,
// quantities never grouped/summed/labelled good-or-rejected, RN8.2/H2). Read-only throughout
// (RN11/S189): no button here ever reports, corrects or cancels production.
// ⚠️ ANCHOS de la tabla de avance, medidos el 23/09 a 1440 (hueco 1.009 px): desbordaba 186 px y el
// chevron —el ÚNICO control que abre el desplegable— quedaba fuera de la vista al abrir. Dos cosas:
//   · el tiempo lleva ancho fijo (TIME_COLUMN, 200 px): lo ensanchaba a 266 px la nota «Sin teórico ·
//     desviación no calculable» sin partir línea; ahora parte (ver `operationRowCells`);
//   · el chevron queda FIJADO a la derecha, como en Workflow: el scroll sigue destapando todo lo demás,
//     y lo que tapa al abrir es el hueco de un icono, nunca una columna entera.
// ⚠️ 160 px left 128 of room after the cell padding, and «8s · Within theoretical» in the numeric
// font needs ~165: it spilled over PROGRESS at every width. The column widens a little AND the text
// wraps (operationRowCells), so neither alone has to be exact.
const TIME_COLUMN = { width: 200, minWidth: 200, maxWidth: 200 }

const EXPAND_COLUMN = { position: 'sticky', right: 0, zIndex: 2, width: 56, minWidth: 56 }

// E2E D3 (30/09): at 1280/1440 px the sticky chevron sat on top of the "%" column because the table
// squeezed its columns under it. The table has a floor equal to the sum of the column minimums, so
// when the container is narrower it SCROLLS instead of squeezing; at scroll end nothing is under the
// chevron, and its opaque background hides what scrolls beneath it.
// Task 29 (E2E round 2, D-A6): with a floor of 842 the table was STILL wider than the block at 1280
// (−134 px at scroll start) and 1440 (−94), because the real widths were set by contents that could
// not wrap — an operation identifier like `0911571000_0100_PRELIMINARY` (no break point in it), the
// nowrap status chip, «Not computable» in the counts cell. User decision: the table FITS at 1280 and
// the chevron stays sticky. So the minimums come down AND those contents wrap (Job and Status cells
// below, the counts cell in `operationRowCells`). TIME stays at 200 (Task 12: its label wraps).
const JOB_COLUMN = { minWidth: 140 }

const STATUS_COLUMN = { minWidth: 120 }

const COUNTS_COLUMN = { minWidth: 88 }

const PROGRESS_COLUMN = { minWidth: 88 }

const PERCENT_COLUMN = { minWidth: 72 }

// Width the table gets at a 1280 px window, from `FabricationOrderDetailDialog.jsx`:
//   1280 viewport
//   − 16  Paper `m: 1` (8 px per side; the E2E measured the Paper at the full 1280, so this is margin)
//        — `maxWidth: max(1320px, 90vw)` = 1320 does not bind below it
//   − 246 nav column (`width: { lg: 246 }`, lg = 1200 px and up)
//   − 48  `block-content` padding (`p: { md: 3 }`, 24 per side)
//   − 15  `block-content` vertical scrollbar (`overflowY: 'auto'`, classic scrollbars)
//   − 2   TableContainer border (1 px per side)
//   = 953
export const TABLE_AVAILABLE_WIDTH_AT_1280 = 1280 - 16 - 246 - 48 - 15 - 2

// The narrowest window that still gets the 246 px nav (lg = 1200): same terms, 873. The browser
// check of fix r1 measured the table 4.5 px too wide there with a floor of 780, hence 764.
export const TABLE_AVAILABLE_WIDTH_AT_1200 = 1200 - 16 - 246 - 48 - 15 - 2

// 140 + 120 + 88 + 200 + 88 + 72 + 56 = 764: ~190 px below 953 and ~110 below 873, room for what
// cannot wrap below its minimum (the four figures, a status word, «computable» in the % column).
export const TABLE_MIN_WIDTH = JOB_COLUMN.minWidth + STATUS_COLUMN.minWidth + COUNTS_COLUMN.minWidth +
  TIME_COLUMN.minWidth + PROGRESS_COLUMN.minWidth + PERCENT_COLUMN.minWidth + EXPAND_COLUMN.minWidth

// An identifier with no break point may break anywhere rather than widen the column.
const JOB_CELL = { pl: 2, overflowWrap: 'anywhere' }

// The chip label may take two lines (MUI keeps it nowrap at a fixed 24 px height otherwise).
const STATUS_CELL = {
  '& .MuiChip-root': { height: 'auto', maxWidth: '100%' },
  '& .MuiChip-label': { whiteSpace: 'normal', py: '2px' }
}

// The advance of each operation of the order (N10 rows, with N3 for status, theoretical time and
// incidences) and, for the open one, its detail. The reports are the container's: `reports` carries
// what the rows need of them.
const OperationsProgressTable = ({ operations, workflowOperations, workflowStatus, onRetryWorkflow, expansionCache: cache, reports }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const { formatDateTime } = useDateFormatter()
  const { expansions, qualities } = cache
  const { reportsOf, reportsStatus, hasMoreReports, loadingMoreReports, loadReports, loadMoreReports } = reports
  const [expandedOperationId, setExpandedOperationId] = useState(null)
  // S432 — lo REAL de cada operación que vivía en el desplegable de Workflow llega en N4, y se pide
  // al desplegar, una vez por operación: lo ya cargado no se vuelve a pedir al cerrar y reabrir (la
  // misma regla que S446 puso en Workflow). S437/S439 — la calidad real llega por su PROPIA puerta y
  // se pide a la vez: si una de las dos falla, la otra se sigue enseñando. Las dos viven en la caché
  // compartida de arriba; aquí se piden las de la operación abierta.
  useEffect(() => {
    if (expandedOperationId === null) return
    cache.ensure('expansions', expandedOperationId)
    cache.ensure('qualities', expandedOperationId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedOperationId, cache.version])
  const toggleOperation = (jobReferenceId) => {
    if (expandedOperationId === jobReferenceId) {
      setExpandedOperationId(null)
      return
    }
    setExpandedOperationId(jobReferenceId)
    cache.retry('expansions', jobReferenceId)
    cache.retry('qualities', jobReferenceId)
  }

  // Las filas de la tabla, ya derivadas: el porcentaje y el estado se calculan UNA vez y de ahí
  // salen tanto la fila como el recuento de la cabecera. Calcularlos dos veces es como acaban
  // discrepando el titular y lo que hay debajo.
  // S431 / S444 — DE DÓNDE SALE CADA CIFRA de la fila (decisión del hub del 23/09, con N10 ya sirviendo
  // lo nuevo):
  //   · lo que TAMBIÉN sale en Workflow —tiempo teórico, estado de la operación, incidencias abiertas—
  //     sale de N3, igual que allí: es la misma cifra y tiene que tener UN SOLO ORIGEN;
  //   · lo que SOLO está en Progress —OK, pendientes, rechazadas y tiempo real— sale de N10.
  // ⚠️ Las incidencias son UNA sola cifra en la fila, de N3: hoy N3 trae `null` y N10 `0` para lo mismo,
  // y Sun está averiguando cuál es el bueno. Sin la fila de N3 (cargando o fallida), lo suyo se declara
  // ausente: no se sustituye por el de N10, que sería otra vez dos orígenes para una cifra.
  const workflowById = new Map((workflowOperations || []).map((row) => [row.jobReferenceId, row]))
  const rowCellsSource = (operation) => {
    const n3 = workflowById.get(operation.jobReferenceId) || {}
    return {
      quantityDone: operation.done,
      quantityPending: operation.pending,
      rejectedQuantity: operation.rejectedQuantity,
      openIncidenceCount: n3.openIncidenceCount,
      theoreticalDurationSeconds: n3.theoreticalDurationSeconds,
      actualDurationSeconds: operation.actualDurationSeconds
    }
  }

  const operationRows = operations.map((operation) => {
  // ADDENDUM 11 (E17): `pending` es lo que QUEDA por reportar, no un objetivo — el nombre
  // anterior (`expected`) invitaba a dividir uno entre otro, y eso es justo lo que se hacía: una
  // operación terminada llegaba como 1/0 y salía "—", y una no empezada como 0/1 y salía "0%".
  // La columna se leía al revés. El divisor son las piezas que LLEGARON: `done + pending`.
    const done = isBlank(operation.done) ? null : operation.done
    const pending = isBlank(operation.pending) ? null : operation.pending
    const reached = done === null || pending === null ? null : done + pending
    // `reached === 0` NO es 0%: la pieza nunca llegó a esta operación (ningún predecesor reportó
    // hacia ese nodo, rama no tomada). Decir "0% completado" afirmaría algo falso sobre una rama que
    // no se tomó — RN8, ausencia no es cero, la misma regla de S132.
    // Floor, not round: 99.5 must not read 100% next to «Partly reported». `pending` can be negative.
    const percent = reached ? Math.min(100, Math.max(0, Math.floor((done / reached) * 100))) : null
    const state = operationReportingState({ done, pending, reached })
    // §2.1 — el segundo eje: el bloqueo se suma al estado con su aviso escrito, sin tocar lo que dice.
    // ⚠️ Add. 64 H/I: las incidencias son de N3, también aquí, como la cifra de la fila y el titular.
    const blocked = Number(rowCellsSource(operation).openIncidenceCount) >= 1
    return { operation, percent, state, blocked, view: operationCodeAndName(operation) }
  })

  // ⚠️ EL RECUENTO SE DERIVA DE LAS FILAS, no de una lista de estados escrita a mano: enumera los
  // grupos que HAY, en el orden en que aparecen. Si mañana se añade un estado al vocabulario, sale
  // solo; con una lista fija se quedaría fuera sin que nadie lo note.
  const stateCounts = operationRows.reduce((groups, row) => {
    const group = groups.find((candidate) => candidate.state === row.state)
    if (group) {
      group.count += 1
      return groups
    }
    return [...groups, { state: row.state, count: 1 }]
  }, [])

  // #3122 — the expanded content is built only for the row that is open: building it for every row
  // on each render walked all the loaded reports several times per row.
  const renderExpansionRow = (operation) => {
    const expansion = expansions[operation.jobReferenceId] || { status: 'idle', data: {} }
    const detail = expansion.data || {}
    // ⚠️⚠️ Add. 64 H/I — el titular lee cada cifra de la MISMA puerta que la fila
    // (`rowCellsSource`): rechazadas y tiempo real de N10, incidencias de N3.
    const figures = rowCellsSource(operation)
    const headlineItems = [
      { testId: 'operation-progress-done', label: t_('Reported OK'), value: isBlank(operation.done) ? t_('Not reported') : operation.done },
      { testId: 'operation-progress-pending', label: t_('Left to report'), value: isBlank(operation.pending) ? t_('Not reported') : operation.pending },
      // S432 — LAS RECHAZADAS Y SU DESTINO. ⚠️ Add. 64 I: la CANTIDAD es la de N10, la de la
      // fila; el DESTINO (SCRAP/REWORK) solo lo trae N4, así que se añade cuando llega.
      // Una pieza desechada y una reprocesada no son el mismo hecho. Y el rojo SOLO con
      // rechazo de verdad (addendum 33 punto 1).
      {
        testId: 'expansion-rejected',
        label: t_('Rejected'),
        value: isBlank(figures.rejectedQuantity)
          ? t_('Not reported')
          : expansion.status === 'ready' && !isBlank(detail.rejectedDisposition)
            ? `${figures.rejectedQuantity} (${t_(detail.rejectedDisposition)})`
            : figures.rejectedQuantity,
        tone: Number(figures.rejectedQuantity) >= 1 ? 'error' : undefined
      },
      // ADDENDUM 13 (E19): "tiempo declarado" decía lo que el dato NO es. Es el tiempo
      // REAL de los pasos cerrados, y "declarado" invitaba a pensar que alguien lo
      // declaró.
      // ⚠️⚠️ ESTE VALOR SALÍA EN SEGUNDOS CRUDOS: «5803» donde todas las demás duraciones
      // de la ficha dicen «1h 36m». No es un formato feo, es un número que no se puede
      // leer — nadie convierte 5.803 segundos de cabeza, y al lado de un teórico ya
      // formateado invitaba a compararlos como si fueran la misma unidad. Faltaba el
      // `formatDuration` que usan las otras once duraciones del detalle.
      { testId: 'operation-progress-actual-time', label: t_('Actual time'), value: isBlank(operation.actualDurationSeconds) ? t_('Not reported') : formatDuration(operation.actualDurationSeconds) },
      // ⚠️⚠️ AQUÍ ESTABAN LOS AUTOCONTROLES «ok/total», y se quitaron (PO, 23/09): salían del
      // VEREDICTO guardado del informe, que en la base del PO es FAIL siempre, así que
      // decían «0 de N» en todas las operaciones. El hueco NO se rellena con un recuento
      // propio de mediciones: sería el mismo resumen de calidad con otro nombre. La calidad
      // de la operación se lee en su pestaña, informe a informe.
      {
      // ⚠️ Mismo criterio que en Operaciones: el aviso SOLO cuando hay incidencias. Un
      // triángulo en todas las operaciones sanas no avisa.
        testId: 'operation-progress-open-incidences',
        label: t_('Open incidences'),
        value: isBlank(figures.openIncidenceCount) ? t_('Not reported') : figures.openIncidenceCount,
        tone: Number(figures.openIncidenceCount) >= 1 ? 'warning' : undefined,
        icon: Number(figures.openIncidenceCount) >= 1
          ? <WarningAmberRounded data-testid={`operation-progress-incidence-warning-${operation.jobReferenceId}`} fontSize='small' sx={{ color: 'warning.main' }} />
          : null
      }
    ]
    const figuresHeadline = (
      <ExpansionHeadline testId={`operation-progress-headline-${operation.jobReferenceId}`} items={headlineItems} />
    )
    // ⚠️⚠️ SOLO SE PLIEGA LO VACÍO, Y UN CERO NO ESTÁ VACÍO (RN8). Un cero es un dato
    // MEDIDO —«0 incidencias abiertas» dice algo, y §2.2 ya lo pinta en gris sin
    // resaltar—, así que basta con que UNA de las cinco traiga valor para que no se
    // pliegue ninguna: plegar solo lo vacío es justo lo que hace que lo lleno destaque,
    // y un desplegable con una sola cifra no se pliega.
    //
    // ⚠️⚠️ SE PREGUNTA CON `isBlank`, NUNCA CON `??` NI CON `=== null`. La frontera
    // (`detailApi.js`) normaliza las ausencias a CADENA VACÍA, y `'' ?? algo` devuelve
    // `''`: un predicado que solo mirara el nulo no plegaría JAMÁS con datos reales, y
    // el test que lo probara escribiendo `{ done: null }` a mano estaría dando por bueno
    // un caso que la aplicación no produce nunca. Por eso los tests de esto pasan por
    // `mapProgressDto`.
    //
    // ⚠️ Las rechazadas cuentan: si N4 trae un rechazo, plegar el titular lo escondería.
    const figuresAbsent = isBlank(operation.done) && isBlank(operation.pending) &&
                isBlank(operation.actualDurationSeconds) && isBlank(figures.openIncidenceCount) &&
                isBlank(figures.rejectedQuantity)
    // El resumen sale de los MISMOS ítems que se pliegan: cada cifra se nombra con su
    // rótulo y con la raya larga de §2.2 en el sitio del valor, que es como este bloque
    // escribe una ausencia. La frase de cabecera la dice una vez en palabras.
    const foldedFigures = figuresAbsent
      ? headlineItems.map((item) => `${item.label} ${EMPTY_DASH}`)
      : []
    // S435 — los materiales consumidos de S421, movidos a su pestaña, agregados por TIPO
    // (no por lote: por lote serían 348 filas de un solo paso).
    const resourcesContent = <OperationResources jobReferenceId={operation.jobReferenceId} resources={operation.resources} />
    // Los reportes de planta de la operación: la tabla de siempre, ahora dentro de su pestaña.
    const renderReports = (reportCount) => {
      const jobId = operation.jobReferenceId
      const jobReports = reportsOf(jobId)
      // N4 says how many reports the operation has; N11 only has the pages loaded so far. An empty
      // filtered list with a positive count is NOT "no reports": the rest is on a page not loaded.
      const total = isBlank(reportCount) ? 0 : Number(reportCount)
      const partial = jobReports.length < total
      const columnas = qualityTypeColumns(jobReports)
      return (
        <Box data-testid={`operation-progress-reports-section-${jobId}`}>
          {reportsStatus === 'error' && jobReports.length === 0 && (
            <Stack direction='row' spacing={1.5} alignItems='center'>
              <Typography data-testid={`operation-progress-reports-error-${jobId}`} variant='body2' sx={{ color: 'error.main' }}>{t_('Could not load the reports')}</Typography>
              <Button size='small' onClick={loadReports} sx={{ textTransform: 'none' }}>{t_('Retry')}</Button>
            </Stack>
          )}
          {reportsStatus === 'loading' && jobReports.length === 0 && (
            <Typography data-testid={`operation-progress-reports-loading-${jobId}`} variant='body2' sx={{ color: 'text.secondary' }}>{t_('Loading...')}</Typography>
          )}
          {reportsStatus === 'ready' && jobReports.length === 0 && !partial && (
            <Typography data-testid={`operation-progress-no-reports-${jobId}`} variant='body2' sx={{ color: 'text.secondary' }}>
              {t_('No reports yet')}
            </Typography>
          )}
          {/* ⚠️⚠️ ERA UNA LÍNEA DE TEXTO CORRIDO por reporte: fecha · operario ·
            «Ok: 1 · Burr: 2». Con un tipo se leía; con tres hay que buscar el
            mismo nombre en cada línea para comparar dos reportes, que es
            exactamente lo que una tabla resuelve. Las COLUMNAS SE DERIVAN de lo
            que llega —el catálogo puede crecer y una lista fija dejaría fuera al
            cuarto tipo sin que nadie lo note— y no se clasifican en buenas y
            rechazadas (S288: el catálogo no lleva esa clasificación).
            ⚠️ Y NO HAY COLUMNA DE TIEMPO: «time is a fact of the operation, never
            of a report» (S173). Vive en el titular, no aquí. */}
          {jobReports.length > 0 && (
            <Table size='small' data-testid={`operation-progress-reports-${jobId}`}>
              <TableHead>
                <TableRow>
                  <TableCell>{t_('Reported at')}</TableCell>
                  <TableCell>{t_('Worker')}</TableCell>
                  {columnas.map((columna) => (
                    <TableCell key={columna.id} align='right'>{columna.name}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {jobReports.map((report) => (
                  <TableRow key={report.reportId} data-testid={`operation-progress-report-${report.reportId}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(report.reportedAt)}</TableCell>
                    {/* §2.2 — la raya larga en vez del literal. ⚠️ Este operario
                      es el del PARTE DE CANTIDAD, otro dato que el de la
                      operación: la excepción del addendum 38 protege el
                      rótulo `Operation workers`, que no es este. */}
                    <TableCell>{report.worker?.workerName || EMPTY_DASH}</TableCell>
                    {columnas.map((columna) => {
                      const cantidad = quantityOfType(report, columna)
                      return (
                        <TableCell key={columna.id} align='right' data-testid={`report-${report.reportId}-qty-${columna.id}`} sx={{ fontFamily: 'monospace' }}>
                          {/* Un reporte que no menciona un tipo NO está diciendo
                            cero de ese tipo: no dice nada (RN8). */}
                          {cantidad === null ? EMPTY_DASH : cantidad}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {reportsStatus === 'ready' && partial && (
            <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mt: 1 }}>
              <Typography data-testid={`operation-progress-reports-partial-${jobId}`} variant='body2' sx={{ color: 'text.secondary' }}>
                {t_('{{shown}} of {{count}} reports loaded', { shown: jobReports.length, count: total })}
              </Typography>
              {hasMoreReports && (
                <Button data-testid={`operation-progress-reports-load-more-${jobId}`} size='small' onClick={loadMoreReports} disabled={loadingMoreReports} sx={{ textTransform: 'none' }}>
                  {loadingMoreReports ? t_('Loading...') : t_('Load more')}
                </Button>
              )}
            </Stack>
          )}
        </Box>
      )
    }
    return (
      <TableRow>
        <TableCell colSpan={7} data-testid={`operation-progress-expansion-${operation.jobReferenceId}`} sx={{ backgroundColor: raisedColor(theme) }}>
          {/* §2.3 — LAS CINCO CIFRAS SE PLIEGAN EN UNA LÍNEA CUANDO NINGUNA TRAE DATO.
                            La misma línea que el pie de General y los grupos vacíos de Documentos,
                            el mismo componente: desplegar una operación sin nada reportado daba
                            cinco huecos que decían «Sin informar» y doscientos píxeles de nada.
                            ⚠️⚠️ PLEGAR NO ES OCULTAR. La línea ENUMERA las cinco CON SU VALOR, una
                            a una, así que las cinco se siguen leyendo SIN abrir nada; lo que se
                            retira es el peso de la rejilla, no el dato. Y «Ver las cifras» devuelve
                            el titular ENTERO — el mismo componente, no una copia.
                            ⚠️ Y SOLO SE PLIEGA EL TITULAR. Los reportes de la operación que van
                            debajo NO entran aquí: son otra cosa y pueden traer datos aunque las
                            cinco cifras falten. */}
          {figuresAbsent
            ? (
              <CollapsedFieldsLine
                testId={`operation-progress-folded-${operation.jobReferenceId}`}
                headline={t_('Nothing reported on this operation yet')}
                summary={foldedFigures}
                openLabel={t_('See the figures')}
              >
                {figuresHeadline}
              </CollapsedFieldsLine>
              )
            : figuresHeadline}
          <Box sx={{ mb: 1.5 }} />
          {/* ⚠️⚠️ S432 — DEBAJO DEL TITULAR, PESTAÑAS: Reportes · Estados · Operarios ·
                            Recursos · Calidad. Todo lo real de la operación, incluido lo que vivía
                            en el desplegable de Workflow (S440: nada se pierde al moverlo). Llegan
                            con N4; mientras carga se dice, y si falla se dice y se enseña lo que no
                            depende de N4 —los reportes y los materiales—, que sigue siendo verdad. */}
          {expansion.status === 'loading' && (
            <Typography data-testid={`operation-progress-detail-loading-${operation.jobReferenceId}`} variant='body2' sx={{ color: 'text.secondary' }}>{t_('Loading...')}</Typography>
          )}
          {expansion.status === 'error' && (
            <Stack spacing={1.5}>
              <Typography data-testid={`operation-progress-detail-error-${operation.jobReferenceId}`} variant='body2' sx={{ color: 'error.main' }}>{t_('Could not load this block')}</Typography>
              {renderReports('')}
              {resourcesContent}
            </Stack>
          )}
          {expansion.status === 'ready' && (
            <ProgressOperationDetails
              data={detail}
              operation={operation}
              figures={rowCellsSource(operation)}
              quality={qualities[operation.jobReferenceId] || { status: 'loading', data: { inspectionReports: [] } }}
              reportsCount={isBlank(detail.reportCount) ? reportsOf(operation.jobReferenceId).length : Number(detail.reportCount)}
              reportsContent={renderReports(detail.reportCount)}
              resourcesContent={resourcesContent}
            />
          )}
        </TableCell>
      </TableRow>
    )
  }

  return (
    <Box data-testid='operations-progress'>
      {/* The row cells come from N3. Without it the status, theoretical time and incidences read as
      absent: a failed N3 is said, with a way to retry, instead of looking like missing data. */}
      {workflowStatus === 'error' && (
        <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 1 }}>
          <Typography data-testid='progress-workflow-error' variant='body2' sx={{ color: 'error.main' }}>{t_('Could not load the operation data')}</Typography>
          <Button data-testid='progress-workflow-retry' size='small' variant='outlined' onClick={onRetryWorkflow} sx={{ textTransform: 'none', borderRadius: CONTROL_RADIUS }}>{t_('Retry')}</Button>
        </Stack>
      )}
      <Stack direction='row' spacing={2} alignItems='baseline' justifyContent='space-between' flexWrap='wrap' sx={{ mb: 1, rowGap: 0.5 }}>
        <Typography variant='caption' sx={{ fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'text.secondary', display: 'block' }}>
          {t_('Advance per operation')}
        </Typography>
        {stateCounts.length > 0 && (
          <Typography data-testid='operations-progress-states' variant='caption' sx={{ color: 'text.secondary' }}>
            {stateCounts.map((group) => `${group.count} ${t_(OPERATION_STATE_VIEWS[group.state].labelKey)}`).join(' · ')}
          </Typography>
        )}
      </Stack>
      <TableContainer sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}` }}>
        <Table size='small' aria-label={t_('Advance per operation')} sx={{ minWidth: TABLE_MIN_WIDTH }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: raisedColor(theme) }}>
              <TableCell sx={JOB_COLUMN}><ColumnHeader>{t_('Job')}</ColumnHeader></TableCell>
              {/* S444 — el ESTADO de la operación, con la MISMA celda que Workflow. No es el estado
              de reporte que va bajo el nombre: aquél es de las cantidades, éste de la operación. */}
              <TableCell sx={STATUS_COLUMN}><ColumnHeader>{t_('Status')}</ColumnHeader></TableCell>
              {/* S431 (D1, D6) — las cuatro cifras y el tiempo real contra el teórico, que vivían en la
              fila de Workflow. SIN columna de personas (D11). */}
              <TableCell sx={COUNTS_COLUMN}><ColumnHeader>{t_('OK / pend. / rej. / inc.')}</ColumnHeader></TableCell>
              <TableCell sx={TIME_COLUMN}><ColumnHeader>{t_('Time vs theoretical')}</ColumnHeader></TableCell>
              <TableCell sx={PROGRESS_COLUMN}><ColumnHeader>{t_('Progress')}</ColumnHeader></TableCell>
              <TableCell align='right' sx={PERCENT_COLUMN}><ColumnHeader>%</ColumnHeader></TableCell>
              <TableCell sx={{ ...EXPAND_COLUMN, backgroundColor: raisedColor(theme) }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {operationRows.map(({ operation, percent, state, blocked, view: operationView }) => {
              const stateView = OPERATION_STATE_VIEWS[state]
              // S389 — LA MISMA FORMA QUE EL DESPLIEGUE DE OPERACIONES (addendum 33 punto 8,
              // extendido a Progreso por el PO): una franja elevada con las cifras grandes en vez
              // de cinco datos planos del mismo tamaño.
              // ⚠️ SE COMPARTE LA FORMA, NO LOS CAMPOS, y `ExpansionHeadline` lo explica: N10 trae
              // `done`, `pending`, tiempo real, autocontroles e incidencias, y NO trae rechazadas,
              // ni dispositivo, ni fechas por operario. Meterlo en un componente con los cinco
              // huecos de Operaciones dejaría tres vacíos aquí, y un hueco vacío en un titular
              // afirma que el dato falta cuando lo que pasa es que esta puerta no lo sirve.
              // ⚠️ Los `testId` NO cambian: son los mismos datos en otra disposición, y
              // renombrarlos habría obligado a reescribir tests que no cambian de significado.
              // 📌 "1 / 0" eran un solo dato y se dividieron en dos con su nombre. El rótulo del
              // segundo es "Left to report" y no "Pendiente": tras el addendum 30 A esa palabra es
              // la CANTIDAD que queda, y la ausencia se dice "Sin informar".
              //
              // 📌 LA LISTA SUBE AQUÍ —antes vivía dentro del JSX— para que el resumen del pliegue
              // se DERIVE de ella y no de una segunda lista escrita al lado. Dos listas paralelas
              // es exactamente cómo una sexta cifra acabaría plegada sin que nadie la nombre.
              return (
                <React.Fragment key={operation.jobReferenceId}>
                  <TableRow data-testid={`operation-progress-${operation.jobReferenceId}`} hover>
                    {/* S442 (D13, plan del 23/09) — aquí iba una barra de color con el estado de
                    reporte y, en rojo, el bloqueo por incidencias. Se quitó: los dos ya se
                    dicen escritos debajo del nombre —el estado y «Incidencias abiertas»—. */}
                    <TableCell sx={JOB_CELL}>
                      <Stack direction='row' spacing={1.5} alignItems='stretch'>
                        <Box sx={{ minWidth: 0 }}>
                          {/* §2.4 — EL CÓDIGO ENCIMA DEL NOMBRE, en una línea mono pequeña, y no
                          fundido con él por un guion. Fundidos («0911571000 · OP 0450 -
                          Preliminary hull disassembly») el ojo tiene que partir la cadena
                          para encontrar cualquiera de los dos; separados, la columna de
                          códigos se escanea sola.
                          ⚠️ La REGLA de qué se enseña no cambia: sigue siendo la simétrica
                          del ADDENDUM 20 (E26), la MISMA función que usan la cabecera y el
                          bloque de Workflow (S353). Lo que cambia es la disposición. */}
                          {operationView.showsBoth && (
                            <Typography data-testid={`operation-progress-code-${operation.jobReferenceId}`} component='span' variant='caption' sx={{ display: 'block', fontFamily: 'monospace', color: 'text.secondary' }}>
                              {operationView.code}
                            </Typography>
                          )}
                          <Typography data-testid={`operation-progress-label-${operation.jobReferenceId}`} component='span' variant='body2' sx={{ display: 'block', fontWeight: 700 }}>
                            {operationView.showsBoth ? operationView.name : operationView.identifier}
                          </Typography>
                          {/* §2.5 — el estado SE DICE: desde S442 es lo único que lo cuenta, y un
                          color sin palabras no distinguiría «falta el dato» de «va con
                          retraso». */}
                          <Typography data-testid={`operation-state-${operation.jobReferenceId}`} component='span' variant='caption' sx={{ display: 'block', color: stateView.text }}>
                            {t_(stateView.labelKey)}
                          </Typography>
                          {blocked && (
                            <Typography data-testid={`operation-blocked-${operation.jobReferenceId}`} component='span' variant='caption' sx={{ display: 'block', fontWeight: 700, color: 'error.main' }}>
                              {t_('Open incidences')}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell data-testid={`operation-progress-status-${operation.jobReferenceId}`} sx={STATUS_CELL}>
                      {/* S444 — de N3, que es quien sirve el estado de la operación HOY. Sin su fila
                      (cargando o fallida) no se inventa: se declara la ausencia. */}
                      {workflowById.has(operation.jobReferenceId)
                        ? <OperationStatusCell row={workflowById.get(operation.jobReferenceId)} />
                        : <Typography data-absent='true' variant='body2' sx={{ color: 'text.disabled' }}>{EMPTY_DASH}</Typography>}
                    </TableCell>
                    {/* S431 — las celdas movidas de Workflow, con sus reglas intactas; de dónde sale
                    cada cifra, en `rowCellsSource`. ⚠️ Durante un rato del 23/09 esta fila leyó
                    de N10 cuando N10 aún no traía el teórico, y salió sin barra ni exceso: un
                    movimiento convertido en regresión (lo vio el PO). */}
                    <TableCell sx={COUNTS_COLUMN}>
                      <OperationQuantityCell row={rowCellsSource(operation)} />
                    </TableCell>
                    <TableCell sx={TIME_COLUMN}>
                      <OperationTimeCell row={rowCellsSource(operation)} />
                    </TableCell>
                    <TableCell sx={PROGRESS_COLUMN}>
                      {/* Sin porcentaje no se pinta barra: una barra vacía se lee igual que un 0%
                      y diría lo mismo que el texto se cuida de no decir. */}
                      {percent === null
                        ? <Box sx={{ height: 8, borderRadius: 999, backgroundColor: alpha(theme.palette.text.primary, 0.06) }} />
                        : (
                          <LinearProgress
                            variant='determinate'
                            value={percent}
                            sx={{ height: 8, borderRadius: 999, backgroundColor: alpha(theme.palette.text.primary, 0.12) }}
                          />
                          )}
                    </TableCell>
                    <TableCell align='right' sx={PERCENT_COLUMN}>
                      {/* ⚠ ADDENDUM 30 A: decía "Pendiente" y ahora dice "No calculable". Esta
                      columna habla del PORCENTAJE, y sin porcentaje lo honrado es decir que no
                      sale, no que esté pendiente de nada.
                      📌 `percent === null` cubre DOS causas —que falte `done` o `pending`, y
                      que `reached` sea 0 porque la pieza nunca llegó— y las dos se dicen igual
                      a propósito: desde esta columna ambas son la misma frase verdadera sobre
                      el porcentaje. Si alguna vez hay que distinguirlas, se distinguen aquí. */}
                      <Typography data-testid={`operation-progress-percent-${operation.jobReferenceId}`} variant='body2' sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                        {percent === null ? t_('Not computable') : `${percent}%`}
                      </Typography>
                    </TableCell>
                    {/* S459 — el fondo de la celda fija es el de la TARJETA (`raisedColor`), como en Workflow: con
                    el del papel se seguía viendo una franja sombreada solo aquí. */}
                    <TableCell align='right' sx={{ ...EXPAND_COLUMN, backgroundColor: raisedColor(theme) }}>
                      {/* Revisión del PO (pasada 24): esto era un icono DECORATIVO — no desplegaba
                    nada — y los cuatro datos de la operación se pintaban en divs con
                    `display: none`, visibles solo para los tests. Ahora el chevron es el
                    control que los abre.
                    S459 — y desde aquí es EL MISMO botón que Workflow, con `ExpandRowButton`:
                    antes era una franja sombreada con `ExpandMoreRounded` rotando 180°, un
                    segundo aspecto para la misma acción. El testid no cambia. */}
                      <ExpandRowButton
                        testId={`expand-operation-progress-${operation.jobReferenceId}`}
                        ariaLabel={t_('Open operation detail {{operation}}', { operation: operationView.identifier })}
                        expanded={expandedOperationId === operation.jobReferenceId}
                        onClick={() => toggleOperation(operation.jobReferenceId)}
                      />
                    </TableCell>
                  </TableRow>
                  {expandedOperationId === operation.jobReferenceId && renderExpansionRow(operation)}
                </React.Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default OperationsProgressTable
